import React from 'react';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { SubSectionHeader } from '../shared/SubSectionHeader';
import { FormField } from '../shared/FormField';
import { formatHandlingTypeForDisplay } from '../../utils/gsmsHandlingTypeUtils';
import classes from '../../bidAnalysisTemplate.module.scss';

interface CogsBuildupSectionProps {
  isExpanded: boolean;
  onToggle: () => void;
  formData: any;
  isDirect: boolean;
  isIndirect: boolean;
  onFieldChange: (field: string, value: any) => void;
  onFieldBlur: (field: string, value: any) => void;
  onFieldKeyDown?: (field: string, e: React.KeyboardEvent) => void;
  gsmsHndlgType?: string;
  packSizeTabs?: Array<{ index: number; packSize: string; conceptName: string }>;
  activePackSizeTab?: number;
}

export const CogsBuildupSection: React.FC<CogsBuildupSectionProps> = ({
  isExpanded,
  onToggle,
  formData,
  isDirect,
  isIndirect: _isIndirect,
  onFieldChange,
  onFieldBlur,
  onFieldKeyDown,
  gsmsHndlgType,
  packSizeTabs,
  activePackSizeTab,
}) => {
  // Helper function to safely parse numeric values and return null if NaN
  const safeParseFloat = (value: any): number | null => {
    if (value === null || value === undefined || value === '') return null;
    const num = typeof value === 'number' ? value : parseFloat(value);
    return isNaN(num) ? null : num;
  };

  const safeParseInt = (value: any): number | null => {
    if (value === null || value === undefined || value === '') return null;
    const num = typeof value === 'number' ? value : parseInt(value);
    return isNaN(num) ? null : num;
  };

  return (
    <div className={classes.subSection}>
      <SubSectionHeader title="Cogs Buildup" isExpanded={isExpanded} onToggle={onToggle} />
      {isExpanded && (
        <div className={classes.subSectionContent}>
          <div className={classes.formGrid4}>
            {/* Common fields for both Direct and Indirect */}
            <FormField label="GSMS Cost From Mfg">
              <InputNumber
                value={safeParseFloat(formData.gsmsCostFromMfg)}
                className={classes.redText}
                onValueChange={e => onFieldChange('gsmsCostFromMfg', e.value)}
                onBlur={e => onFieldBlur('gsmsCostFromMfg', e)}
                onKeyDown={e => onFieldKeyDown?.('gsmsCostFromMfg', e)}
                placeholder="$"
                mode="decimal"
                minFractionDigits={2}
                maxFractionDigits={2}
                min={0}
              />
            </FormField>

            <FormField label="GSMS Net Cost From Mfg">
              <InputNumber
                value={safeParseFloat(formData.gsmsNetCostFromMfg)}
                className={classes.redText}
                readOnly
                disabled
                placeholder="$"
                mode="decimal"
                minFractionDigits={2}
                maxFractionDigits={2}
                min={0}
              />
            </FormField>

            {/* Direct Product Only Fields */}
            {isDirect && (
              <>
                <FormField label="MFG Bottle Size">
                  <InputNumber
                    value={safeParseInt(formData.mfgBottleSize)}
                    onValueChange={e => onFieldChange('mfgBottleSize', e.value)}
                    onBlur={e => onFieldBlur('mfgBottleSize', e)}
                    onKeyDown={e => onFieldKeyDown?.('mfgBottleSize', e)}
                    mode="decimal"
                    useGrouping={false}
                    min={0}
                  />
                </FormField>

                <FormField label="GSMS Pack Size">
                  <InputNumber
                    value={(() => {
                      // GSMS Pack Size is readonly - get value from active pack size tab
                      if (
                        packSizeTabs &&
                        activePackSizeTab !== undefined &&
                        activePackSizeTab !== null
                      ) {
                        const activeTab = packSizeTabs.find(tab => tab.index === activePackSizeTab);
                        if (activeTab && activeTab.packSize) {
                          return safeParseInt(activeTab.packSize);
                        }
                      }
                      return safeParseInt(formData.gsmsPackSize);
                    })()}
                    readOnly
                    disabled
                    mode="decimal"
                    useGrouping={false}
                    min={0}
                  />
                </FormField>

                <FormField label="GSMS Handling Type">
                  <InputText
                    value={
                      gsmsHndlgType || formData.gsmsHndlgType
                        ? formatHandlingTypeForDisplay(gsmsHndlgType || formData.gsmsHndlgType)
                        : ''
                    }
                    readOnly
                    disabled
                    placeholder="Select from Project Information"
                  />
                </FormField>

                <FormField label="GSMS Bottle Capacity">
                  {(() => {
                    const bottleCapacityValue = formData.gsmsBottleCapacity ?? '';

                    return <InputText value={bottleCapacityValue} readOnly disabled />;
                  })()}
                </FormField>

                <FormField label="GSMS Bottle Cost">
                  <InputNumber
                    value={safeParseFloat(formData.gsmsBottleCost)}
                    readOnly
                    disabled
                    mode="decimal"
                    minFractionDigits={2}
                    maxFractionDigits={3}
                    min={0}
                  />
                </FormField>

                <FormField label="GSMS CAP Cost">
                  <InputNumber
                    value={safeParseFloat(formData.gsmsCapCost)}
                    readOnly
                    disabled
                    mode="decimal"
                    minFractionDigits={2}
                    maxFractionDigits={3}
                    min={0}
                  />
                </FormField>

                <FormField label="GSMS Label Cost">
                  <InputNumber
                    value={safeParseFloat(formData.gsmsLabelCost)}
                    readOnly
                    disabled
                    mode="decimal"
                    minFractionDigits={2}
                    maxFractionDigits={3}
                    min={0}
                  />
                </FormField>

                <FormField label="GSMS Insert Cost">
                  <InputNumber
                    value={safeParseFloat(formData.gsmsInsertCost)}
                    readOnly
                    disabled
                    mode="decimal"
                    minFractionDigits={2}
                    maxFractionDigits={3}
                    min={0}
                  />
                </FormField>
              </>
            )}

            {/* Common IFF Fee field */}
            <FormField label="GSMS IFF Fee">
              <InputNumber
                value={safeParseFloat(formData.gsmsIffFee)}
                readOnly
                disabled
                mode="decimal"
                minFractionDigits={2}
                maxFractionDigits={2}
                min={0}
              />
            </FormField>

            {/* Direct Only fields continued */}
            {isDirect && (
              <>
                <FormField label="GSMS Box/carton Cost">
                  <InputNumber
                    value={safeParseFloat(formData.gsmsBoxCartonCost)}
                    readOnly
                    disabled
                    mode="decimal"
                    minFractionDigits={2}
                    maxFractionDigits={3}
                    min={0}
                  />
                </FormField>

                <FormField label="GSMS Desiccant Cost">
                  <InputNumber
                    value={safeParseFloat(formData.gsmsDesiccantCost)}
                    onValueChange={e => onFieldChange('gsmsDesiccantCost', e.value)}
                    onBlur={e => onFieldBlur('gsmsDesiccantCost', e)}
                    onKeyDown={e => onFieldKeyDown?.('gsmsDesiccantCost', e)}
                    mode="decimal"
                    minFractionDigits={2}
                    maxFractionDigits={2}
                    min={0}
                  />
                </FormField>

                <FormField label="Outsource Repack Fee">
                  <InputNumber
                    value={safeParseFloat(formData.outsourceRepackFee)}
                    readOnly
                    disabled
                    mode="decimal"
                    minFractionDigits={2}
                    maxFractionDigits={2}
                    min={0}
                  />
                </FormField>

                <FormField label="Blended WHLS Fee">
                  <InputNumber
                    value={safeParseFloat(formData.blendedWhlsFee)}
                    className={classes.redText}
                    readOnly
                    disabled
                    placeholder="$"
                    mode="decimal"
                    minFractionDigits={2}
                    maxFractionDigits={2}
                    min={0}
                  />
                </FormField>

                <FormField label="GSMS FRT. Cost">
                  <InputNumber
                    value={safeParseFloat(formData.gsmsFrtCost)}
                    readOnly
                    disabled
                    mode="decimal"
                    minFractionDigits={2}
                    maxFractionDigits={5}
                    min={0}
                  />
                </FormField>

                <FormField label="PV 2%">
                  <InputNumber
                    value={safeParseFloat(formData.pv2Percent)}
                    readOnly
                    disabled
                    mode="decimal"
                    minFractionDigits={2}
                    maxFractionDigits={2}
                    min={0}
                  />
                </FormField>

                <FormField label="GSMS Labor">
                  <InputNumber
                    value={safeParseFloat(formData.gsmsLabor)}
                    readOnly
                    disabled
                    mode="decimal"
                    minFractionDigits={2}
                    maxFractionDigits={5}
                    min={0}
                  />
                </FormField>
              </>
            )}

            {/* Common GSMS Cost field */}
            <FormField label="GSMS Cost">
              <InputNumber
                value={safeParseFloat(formData.gsmsCost)}
                readOnly
                disabled
                mode="decimal"
                minFractionDigits={2}
                maxFractionDigits={2}
                min={0}
              />
            </FormField>
          </div>
        </div>
      )}
    </div>
  );
};
